import { Organization, NewOrganization, OrganizationTypeInternal } from '../types/organization';

export const isDemoMode = (): boolean => {
  try {
    return String(process.env.REACT_APP_ENVIRONMENT).toLowerCase() === 'demo';
  } catch (_err) {
    return false;
  }
};

export interface DemoQuote {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  brokerEmail?: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  agency?: string;
  s3Key: string;
  proposalGenerated?: boolean; // Track whether automated proposal generation has occurred
}

const LS_QUOTES_KEY = 'demo.quotes';

export const demoQuotesStore = {
  readAll(): DemoQuote[] {
    const raw = localStorage.getItem(LS_QUOTES_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      const list: DemoQuote[] = Array.isArray(parsed) ? parsed : [];
      // Prune legacy seed: "Eggleston Test"
      const cleaned = list.filter((q) => {
        const company = String((q as any).companyName || '').toLowerCase();
        const employer = String((q as any).employerName || '').toLowerCase();
        return company !== 'eggleston test' && employer !== 'eggleston test';
      });
      if (cleaned.length !== list.length) {
        // persist cleanup so it doesn't reappear
        localStorage.setItem(LS_QUOTES_KEY, JSON.stringify(cleaned));
      }
      return cleaned;
    } catch {
      return [];
    }
  },
  writeAll(quotes: DemoQuote[]): void {
    localStorage.setItem(LS_QUOTES_KEY, JSON.stringify(quotes));
  },
  upsert(quote: DemoQuote): void {
    const all = demoQuotesStore.readAll();
    const idx = all.findIndex(q => q.submissionId === quote.submissionId);
    if (idx >= 0) {
      all[idx] = quote;
    } else {
      all.unshift(quote);
    }
    demoQuotesStore.writeAll(all);
  },
  updateStatus(submissionId: string, status: string): void {
    const all = demoQuotesStore.readAll().map(q => q.submissionId === submissionId ? { ...q, status } : q);
    demoQuotesStore.writeAll(all);
  },
  updateProposalGenerated(submissionId: string, proposalGenerated: boolean): void {
    const all = demoQuotesStore.readAll().map(q => q.submissionId === submissionId ? { ...q, proposalGenerated } : q);
    demoQuotesStore.writeAll(all);
  }
};

export const seedDemoQuotesIfEmpty = (): void => {
  if (demoQuotesStore.readAll().length > 0) return;
  demoQuotesStore.writeAll([]);
};

// Ensure we have a consistent set of demo quotes with Cart & Co as the only sold case
export const ensureDemoSoldQuotes = (): DemoQuote[] => {
  const all = demoQuotesStore.readAll();

  // Predefined demo quotes with Cart & Co as the only sold case, others as In Progress
  // Note: Legacy Metals is not included here as it's meant to be submitted by users for the automated proposal demo
  const predefinedDemoQuotes: DemoQuote[] = [
    {
      submissionId: 'demo-quote-1',
      companyName: "Sam's Auto",
      transperraRep: 'Waleed Bahouth',
      ichraEffectiveDate: '2025-10-01',
      pepm: '95',
      status: 'In Progress',
      brokerName: 'ACC',
      brokerEmail: 'waleed.bahouth@kyndly.com',
      employerName: "Sam's Auto",
      brokerId: 'broker_sam',
      employerId: 'emp_sam',
      agency: '-',
      s3Key: 'demo/quotes/1'
    },
    {
      submissionId: 'demo-quote-3',
      companyName: 'Cart & Co',
      transperraRep: 'Waleed Bahouth',
      ichraEffectiveDate: '2025-10-01',
      pepm: '70',
      status: 'Sold Case',
      brokerName: 'Cart',
      brokerEmail: 'waleed.bahouth@kyndly.com',
      employerName: 'Cart & Co',
      brokerId: 'broker_cart',
      employerId: 'emp_cart',
      agency: '-',
      s3Key: 'demo/sold/3'
    },
    {
      submissionId: 'demo-quote-4',
      companyName: "Salvage Co",
      transperraRep: 'Erik Anderson',
      ichraEffectiveDate: '2025-10-01',
      pepm: '70',
      status: 'In Progress',
      brokerName: 'demo-broker',
      brokerEmail: 'erik.anderson@kyndly.com',
      employerName: "Salvage Co",
      brokerId: 'broker_salvage',
      employerId: 'emp_salvage',
      agency: '-',
      s3Key: 'demo/quotes/4'
    }
  ];

  // Only add predefined demo quotes if they don't already exist
  // This preserves newly submitted quotes while ensuring demo quotes exist
  const existingIds = all.map(q => q.submissionId);
  const missingDemoQuotes = predefinedDemoQuotes.filter(
    demoQuote => !existingIds.includes(demoQuote.submissionId)
  );
  
  if (missingDemoQuotes.length > 0) {
    // Add missing demo quotes without overwriting existing ones
    const updatedQuotes = [...all, ...missingDemoQuotes];
    demoQuotesStore.writeAll(updatedQuotes);
  }
  
  // Return only the sold cases from all quotes (both demo and user-submitted)
  const allQuotes = demoQuotesStore.readAll();
  return allQuotes.filter(q => q.status === 'Sold Case');
};

// ---------------- Proposals (Demo) ----------------
export interface DemoProposal {
  id: string;
  submissionId: string;
  title: string;
  createdAt: string;
  author: string;
  url?: string;
  notes?: string;
}

const LS_PROPOSALS_KEY = 'demo.proposals';

export const demoProposalsStore = {
  readAll(): DemoProposal[] {
    const raw = localStorage.getItem(LS_PROPOSALS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  writeAll(proposals: DemoProposal[]): void {
    localStorage.setItem(LS_PROPOSALS_KEY, JSON.stringify(proposals));
  },
  listBySubmission(submissionId: string): DemoProposal[] {
    return this.readAll().filter(p => p.submissionId === submissionId);
  },
  add(proposal: DemoProposal): void {
    const all = this.readAll();
    all.unshift(proposal);
    this.writeAll(all);
  },
  ensureSeedForSubmission(submissionId: string, companyName: string): DemoProposal[] {
    const existing = this.listBySubmission(submissionId);
    if (existing.length > 0) return existing;
    
    // Only create proposals for Legacy Metals
    if (companyName !== 'Legacy Metals') {
      return [];
    }
    
    const seeded: DemoProposal = {
      id: `proposal-${Date.now()}`,
      submissionId,
      title: `Initial Proposal for ${companyName}`,
      createdAt: new Date().toISOString(),
      author: 'Kyndly Team',
      url: '/documents/Legacy_Metals_ICHRA_Summary_LM855597.pdf',
      notes: 'Auto-generated demo proposal'
    };
    this.add(seeded);
    return [seeded];
  }
};

// ---------------- Demo Documents ----------------
export interface DemoDocument {
  documentId: string;
  submissionId: string;
  filename: string;
  name: string;
  type: string;
  size: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
  companyName: string;
  brokerId: string;
  employerId: string;
  s3Key: string;
  downloadUrl?: string;
}

const LS_DOCUMENTS_KEY = 'demo.documents';

export const demoDocumentsStore = {
  readAll(): DemoDocument[] {
    const raw = localStorage.getItem(LS_DOCUMENTS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  
  writeAll(documents: DemoDocument[]): void {
    localStorage.setItem(LS_DOCUMENTS_KEY, JSON.stringify(documents));
  },
  
  addForSubmission(submissionId: string, files: File[], quote: DemoQuote, uploadedBy: string = 'Demo User'): DemoDocument[] {
    const existingDocs = this.readAll();
    const newDocs: DemoDocument[] = [];
    
    files.forEach((file, index) => {
      const docId = `doc-${submissionId}-${Date.now()}-${index}`;
      const doc: DemoDocument = {
        documentId: docId,
        submissionId: submissionId,
        filename: file.name,
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: formatFileSize(file.size),
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy: uploadedBy,
        companyName: quote.companyName,
        brokerId: quote.brokerId,
        employerId: quote.employerId,
        s3Key: `demo/submissions/${quote.brokerId}/${quote.employerId}/${submissionId}/${file.name}`,
        downloadUrl: `#demo-file-${docId}`
      };
      newDocs.push(doc);
    });
    
    this.writeAll([...existingDocs, ...newDocs]);
    return newDocs;
  },
  
  getBySubmission(submissionId: string): DemoDocument[] {
    return this.readAll().filter(doc => doc.submissionId === submissionId);
  },
  
  deleteBySubmission(submissionId: string): void {
    const filtered = this.readAll().filter(doc => doc.submissionId !== submissionId);
    this.writeAll(filtered);
  }
};

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

// Seed demo documents for existing quotes
export const seedDemoDocumentsIfEmpty = (): void => {
  const existingDocs = demoDocumentsStore.readAll();
  if (existingDocs.length > 0) return;
  
  const quotes = demoQuotesStore.readAll();
  const soldQuotes = quotes.filter(q => q.status === 'Sold');
  
  // Add sample documents for sold quotes
  soldQuotes.forEach(quote => {
    const sampleDocs: DemoDocument[] = [
      {
        documentId: `doc-${quote.submissionId}-census`,
        submissionId: quote.submissionId,
        filename: `${quote.companyName.replace(/\s+/g, '_')}_Census.xlsx`,
        name: `${quote.companyName} Census`,
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        size: '245 KB',
        fileSize: 250880,
        uploadedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
        uploadedBy: 'Demo User',
        companyName: quote.companyName,
        brokerId: quote.brokerId,
        employerId: quote.employerId,
        s3Key: `demo/submissions/${quote.brokerId}/${quote.employerId}/${quote.submissionId}/census.xlsx`,
        downloadUrl: '#demo-census'
      },
      {
        documentId: `doc-${quote.submissionId}-proposal`,
        submissionId: quote.submissionId,
        filename: `${quote.companyName.replace(/\s+/g, '_')}_ICHRA_Proposal.pdf`,
        name: `${quote.companyName} ICHRA Proposal`,
        type: 'application/pdf',
        size: '1.2 MB',
        fileSize: 1258291,
        uploadedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
        uploadedBy: 'Kyndly Team',
        companyName: quote.companyName,
        brokerId: quote.brokerId,
        employerId: quote.employerId,
        s3Key: `demo/submissions/${quote.brokerId}/${quote.employerId}/${quote.submissionId}/proposal.pdf`,
        downloadUrl: '#demo-proposal'
      }
    ];
    
    const existingDocs = demoDocumentsStore.readAll();
    demoDocumentsStore.writeAll([...existingDocs, ...sampleDocs]);
  });
};

// ---------------- Pending Enrollments (Demo) ----------------
export interface DemoEnrollment {
  id: string;
  effectiveDate: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  zip?: string;
  employer: string;
  healthSherpaUrl?: string;
}

const LS_ENROLLMENTS_KEY = 'demo.enrollments.pending';

export const demoEnrollmentsStore = {
  readAll(): DemoEnrollment[] {
    const raw = localStorage.getItem(LS_ENROLLMENTS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  writeAll(rows: DemoEnrollment[]): void {
    localStorage.setItem(LS_ENROLLMENTS_KEY, JSON.stringify(rows));
  }
};

export const seedDemoEnrollmentsIfEmpty = (): void => {
  // Always reset to ensure consistent data - all employees under Cart & Co with matching effective date
  const sample: DemoEnrollment[] = [
    { id: 'enr-1', effectiveDate: '2025-10-01', firstName: 'John', lastName: 'Smith', email: 'john.smith@cartco.com', phone: '5625551234', zip: '85022', employer: 'Cart & Co', healthSherpaUrl: 'https://www.healthsherpa.com/' },
    { id: 'enr-2', effectiveDate: '2025-10-01', firstName: 'Sarah', lastName: 'Johnson', email: 'sarah.j@cartco.com', phone: '5625552345', zip: '85027', employer: 'Cart & Co', healthSherpaUrl: 'https://www.healthsherpa.com/' },
    { id: 'enr-3', effectiveDate: '2025-10-01', firstName: 'Michael', lastName: 'Brown', email: 'm.brown@cartco.com', phone: '5625553456', zip: '85035', employer: 'Cart & Co', healthSherpaUrl: 'https://www.healthsherpa.com/' },
    { id: 'enr-4', effectiveDate: '2025-10-01', firstName: 'Emily', lastName: 'Davis', email: 'emily.d@cartco.com', phone: '5625554567', zip: '85040', employer: 'Cart & Co', healthSherpaUrl: 'https://www.healthsherpa.com/' },
  ];
  demoEnrollmentsStore.writeAll(sample);
};

// ---------------- Demo Brokers ----------------
export interface DemoBroker {
  id: string;
  name: string;
  agency?: string;
  email?: string;
  phoneNumber?: string;
}

const LS_BROKERS_KEY = 'demo.brokers';

export const demoBrokersStore = {
  readAll(): DemoBroker[] {
    const raw = localStorage.getItem(LS_BROKERS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  
  writeAll(brokers: DemoBroker[]): void {
    localStorage.setItem(LS_BROKERS_KEY, JSON.stringify(brokers));
  },
  
  add(broker: DemoBroker): void {
    const all = this.readAll();
    // Check if broker with same id already exists
    const existingIndex = all.findIndex(b => b.id === broker.id);
    if (existingIndex >= 0) {
      all[existingIndex] = broker;
    } else {
      all.unshift(broker);
    }
    this.writeAll(all);
  },
  
  remove(id: string): void {
    const filtered = this.readAll().filter(broker => broker.id !== id);
    this.writeAll(filtered);
  },
  
  findById(id: string): DemoBroker | undefined {
    return this.readAll().find(broker => broker.id === id);
  }
};

export const seedDemoBrokersIfEmpty = (): void => {
  if (demoBrokersStore.readAll().length > 0) return;

  const defaultBrokers: DemoBroker[] = [
    { id: 'demo-broker', name: 'Demo Broker', agency: 'Demo Agency' }
  ];

  demoBrokersStore.writeAll(defaultBrokers);
};

// ---------------- Demo Users ----------------
export interface DemoUser {
  username: string;
  email: string;
  name: string;
  role: string;
  tpaId?: string;
  tpaName?: string;
  brokerId?: string;
  brokerName?: string;
  agency?: string;
  employerId?: string;
  employerName?: string;
  status?: string;
  enabled?: boolean;
  phoneNumber?: string;
}

const LS_USERS_KEY = 'demo.users';

export const demoUsersStore = {
  readAll(): DemoUser[] {
    const raw = localStorage.getItem(LS_USERS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  writeAll(users: DemoUser[]): void {
    localStorage.setItem(LS_USERS_KEY, JSON.stringify(users));
  },

  add(user: DemoUser): void {
    const all = this.readAll();
    // Check if user with same username already exists
    const existingIndex = all.findIndex(u => u.username === user.username);
    if (existingIndex >= 0) {
      all[existingIndex] = user;
    } else {
      all.unshift(user);
    }
    this.writeAll(all);
  },

  update(username: string, userData: Partial<DemoUser>): void {
    const all = this.readAll();
    const index = all.findIndex(u => u.username === username);
    if (index >= 0) {
      all[index] = { ...all[index], ...userData };
      this.writeAll(all);
    }
  },

  remove(username: string): void {
    const filtered = this.readAll().filter(user => user.username !== username);
    this.writeAll(filtered);
  },

  findByUsername(username: string): DemoUser | undefined {
    return this.readAll().find(user => user.username === username);
  },

  findByTpaId(tpaId: string): DemoUser[] {
    return this.readAll().filter(user => user.tpaId === tpaId);
  }
};

export const seedDemoUsersIfEmpty = (): void => {
  if (demoUsersStore.readAll().length > 0) return;

  const defaultUsers: DemoUser[] = [
    // Admin users
    {
      username: 'admin',
      email: 'admin@kyndly.com',
      name: 'System Admin',
      role: 'admin',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0100'
    },
    {
      username: 'john.admin',
      email: 'john.admin@kyndly.com',
      name: 'John Administrator',
      role: 'admin',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0101'
    },

    // TPA Admin users
    {
      username: 'tpa.admin',
      email: 'tpa.admin@demo-tpa.com',
      name: 'Sarah Thompson',
      role: 'tpa_admin',
      tpaId: 'tpa_demo',
      tpaName: 'Demo TPA Group',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0200'
    },
    {
      username: 'mike.tpa',
      email: 'mike.tpa@demo-tpa.com',
      name: 'Mike Patterson',
      role: 'tpa_admin',
      tpaId: 'tpa_demo',
      tpaName: 'Demo TPA Group',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0201'
    },

    // TPA Users (regular)
    {
      username: 'lisa.tpa',
      email: 'lisa.analyst@demo-tpa.com',
      name: 'Lisa Martinez',
      role: 'tpa_user',
      tpaId: 'tpa_demo',
      tpaName: 'Demo TPA Group',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0210'
    },
    {
      username: 'david.tpa',
      email: 'david.analyst@demo-tpa.com',
      name: 'David Chen',
      role: 'tpa_user',
      tpaId: 'tpa_demo',
      tpaName: 'Demo TPA Group',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0211'
    },

    // Broker users
    {
      username: 'broker.acc',
      email: 'waleed.bahouth@kyndly.com',
      name: 'Waleed Bahouth',
      role: 'broker',
      brokerId: 'broker_acc',
      brokerName: 'ACC',
      agency: 'ACC Insurance',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0300'
    },
    {
      username: 'broker.demo',
      email: 'demo.broker@example.com',
      name: 'Demo Broker',
      role: 'broker',
      brokerId: 'demo-broker',
      brokerName: 'Demo Broker',
      agency: 'Demo Agency',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0301'
    },
    {
      username: 'jennifer.broker',
      email: 'jennifer@insurancepros.com',
      name: 'Jennifer Wilson',
      role: 'broker',
      brokerId: 'broker_pros',
      brokerName: 'Insurance Pros',
      agency: 'Insurance Professionals LLC',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0302'
    },

    // Employer users
    {
      username: 'employer.sams',
      email: 'hr@samsauto.com',
      name: 'Robert Johnson',
      role: 'employer',
      employerId: 'emp_sam',
      employerName: "Sam's Auto",
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0400'
    },
    {
      username: 'employer.cart',
      email: 'benefits@cartandco.com',
      name: 'Emily Davis',
      role: 'employer',
      employerId: 'emp_cart',
      employerName: 'Cart & Co',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0401'
    },
    {
      username: 'employer.tech',
      email: 'hr@techcorp.com',
      name: 'Michael Brown',
      role: 'employer',
      employerId: 'emp_tech',
      employerName: 'TechCorp Solutions',
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: '+1-555-0402'
    },

    // Some disabled users for testing
    {
      username: 'disabled.user',
      email: 'disabled@example.com',
      name: 'Disabled User',
      role: 'broker',
      brokerId: 'broker_old',
      brokerName: 'Old Broker',
      agency: 'Inactive Agency',
      status: 'CONFIRMED',
      enabled: false,
      phoneNumber: '+1-555-0999'
    }
  ];

  demoUsersStore.writeAll(defaultUsers);
};

// ---------------- Demo Pipeline Metrics ----------------
export interface DemoPipelineMetrics {
  activeQuotesTotal: {
    count: number;
    membership: number;
    percentALES: string;
    revenue: string;
  };
  activeQuotesSavings: {
    count: number;
    membership: number;
    percentALES: string;
    revenue: string;
  };
  closeRatios: {
    allTime: string;
    priorQuarter: string;
    past90: string;
  };
  highestOpps: Array<{
    name: string;
    estRev: string;
    employees: string;
  }>;
}

const LS_PIPELINE_KEY = 'demo.pipeline';

export const demoPipelineStore = {
  read(): DemoPipelineMetrics | null {
    const raw = localStorage.getItem(LS_PIPELINE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  write(data: DemoPipelineMetrics): void {
    localStorage.setItem(LS_PIPELINE_KEY, JSON.stringify(data));
  }
};

export const seedDemoPipelineIfEmpty = (): DemoPipelineMetrics => {
  const existing = demoPipelineStore.read();
  if (existing) return existing;

  const defaultData: DemoPipelineMetrics = {
    activeQuotesTotal: { count: 45, membership: 900, percentALES: '13.3%', revenue: '$0.81m' },
    activeQuotesSavings: { count: 36, membership: 735, percentALES: '8.3%', revenue: '$0.66m' },
    closeRatios: { allTime: '7.6%', priorQuarter: '11.5%', past90: '12.3%' },
    highestOpps: [
      { name: "Sam's Auto", estRev: '$180k', employees: '200' },
      { name: 'Salvage Co', estRev: '$83k', employees: '92' },
      { name: 'Transylvania Suppliers', estRev: '$30k', employees: '33' },
      { name: 'Progressive Co', estRev: '$29k', employees: '32' },
    ]
  };

  demoPipelineStore.write(defaultData);
  return defaultData;
};

// ---------------- Demo Sold Cases ----------------
export interface DemoSoldCases {
  y2025: {
    groups: number;
    expectedEnrollment: string;
    annualRev: string;
    renewal: string;
  };
  y2026: {
    groups: number;
    expectedEnrollment: string;
    annualRev: string;
    renewal: string;
  };
}

const LS_SOLD_CASES_KEY = 'demo.soldCases';

export const demoSoldCasesStore = {
  read(): DemoSoldCases | null {
    const raw = localStorage.getItem(LS_SOLD_CASES_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  write(data: DemoSoldCases): void {
    localStorage.setItem(LS_SOLD_CASES_KEY, JSON.stringify(data));
  }
};

export const seedDemoSoldCasesIfEmpty = (): DemoSoldCases => {
  const existing = demoSoldCasesStore.read();
  if (existing) return existing;

  const defaultData: DemoSoldCases = {
    y2025: { groups: 53, expectedEnrollment: '1,503', annualRev: '$1.35m', renewal: '43%' },
    y2026: { groups: 33, expectedEnrollment: '806', annualRev: '$0.72m', renewal: '' }
  };

  demoSoldCasesStore.write(defaultData);
  return defaultData;
};

// ---------------- Demo Enrollments Data (Enhanced) ----------------
export interface DemoEnrollmentsData {
  y2025: {
    employees: number;
    dependents: number;
    total: number;
    pending: number;
  };
  y2026: {
    employees: number;
    dependents: number;
    total: number;
  };
}

const LS_ENROLLMENTS_DATA_KEY = 'demo.enrollments.data';

export const demoEnrollmentsDataStore = {
  read(): DemoEnrollmentsData | null {
    const raw = localStorage.getItem(LS_ENROLLMENTS_DATA_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  write(data: DemoEnrollmentsData): void {
    localStorage.setItem(LS_ENROLLMENTS_DATA_KEY, JSON.stringify(data));
  }
};

export const seedDemoEnrollmentsDataIfEmpty = (): DemoEnrollmentsData => {
  const existing = demoEnrollmentsDataStore.read();
  if (existing) return existing;

  const defaultData: DemoEnrollmentsData = {
    y2025: { employees: 872, dependents: 512, total: 1484, pending: 4 },
    y2026: { employees: 510, dependents: 290, total: 800 }
  };

  demoEnrollmentsDataStore.write(defaultData);
  return defaultData;
};

// ---------------- Demo Organizations ----------------
export interface DemoOrganization {
  id: string;
  name: string;
  organization_type: OrganizationTypeInternal;
  created_at: string;
  updated_at: string;
}

const LS_ORGANIZATIONS_KEY = 'demo.organizations';

export const demoOrganizationsStore = {
  readAll(): DemoOrganization[] {
    const raw = localStorage.getItem(LS_ORGANIZATIONS_KEY);
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  },

  writeAll(orgs: DemoOrganization[]): void {
    localStorage.setItem(LS_ORGANIZATIONS_KEY, JSON.stringify(orgs));
  },

  create(org: NewOrganization): DemoOrganization {
    const newOrg: DemoOrganization = {
      id: `org_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: org.name,
      organization_type: org.organization_type,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    const all = this.readAll();
    all.push(newOrg);
    this.writeAll(all);
    return newOrg;
  },

  update(id: string, updates: Partial<Organization>): DemoOrganization {
    const all = this.readAll();
    const index = all.findIndex(o => o.id === id);
    if (index === -1) throw new Error('Organization not found');

    // Prevent changing Kyndly org type
    if (all[index].organization_type === 'admin' && updates.organization_type) {
      throw new Error('Cannot change Kyndly organization type');
    }

    all[index] = {
      ...all[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    this.writeAll(all);
    return all[index];
  },

  delete(id: string): void {
    const all = this.readAll();
    const org = all.find(o => o.id === id);

    // Prevent deleting Kyndly org
    if (org?.organization_type === 'admin') {
      throw new Error('Cannot delete Kyndly organization');
    }

    const filtered = all.filter(o => o.id !== id);
    this.writeAll(filtered);
  }
};

export const seedDemoOrganizationsIfEmpty = (): void => {
  if (demoOrganizationsStore.readAll().length > 0) return;

  const predefined: DemoOrganization[] = [
    {
      id: 'kyndly-admin-org',
      name: 'Kyndly',
      organization_type: 'admin',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-tpa-1',
      name: 'Demo TPA Organization',
      organization_type: 'tpa',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-broker-1',
      name: 'Demo Broker Inc',
      organization_type: 'broker',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-employer-1',
      name: 'Acme Employer Corp',
      organization_type: 'employer',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ];

  demoOrganizationsStore.writeAll(predefined);
};
